import { isEmpty, size } from 'lodash';
import requester from 'utils/requester';
import api from 'config/api';
import Platform from 'platform';
import { getAuthorizedEndpointUrl } from 'helpers/urlhelper';
import { showLoader, hideLoader } from 'actions/loader';
import { LIST_MAX_SIZE as ITEMS_PER_REQUEST } from 'config';
import { selectQuery, selectCategoryById } from 'reducers/search-all/selectors';
import { getIsLoggedInSelector } from 'reducers/profile/selectors';
import * as types from './types';

const getSeriesParams = ({ query, page, isLoggedIn }) => ({
  method: 'get',
  url: `${api.host}${getAuthorizedEndpointUrl(api.endpoints.searchVodPublic, isLoggedIn)}`,
  appendParams: false,
  params: {
    'f_title.titleORtitle.titleBriefORtitle.summaryShortORtitle.summaryLong': query,
    type: 'TV_SERIES',
    f_parent: 'isNull',
    'f_metadata.contentType': 'not(EPISODE)',
    sort: 'relevance,videoContent',
    f_deviceType: Platform.getDeviceType(),
    'f_tenants.id': 1,
    detailed: true,
    searchMetadata: true,
    size: ITEMS_PER_REQUEST,
    page,
  },
});

const getFilmsParams = ({ query, page, isLoggedIn }) => ({
  method: 'get',
  url: `${api.host}${getAuthorizedEndpointUrl(api.endpoints.searchVodPublic, isLoggedIn)}`,
  appendParams: false,
  params: {
    'f_title.titleORtitle.titleBriefORtitle.summaryShortORtitle.summaryLong': query,
    type: 'VOD_ASSET',
    f_parent: 'isNull',
    'f_metadata.contentType': 'MOVIE',
    sort: 'relevance,videoContent',
    f_deviceType: Platform.getDeviceType(),
    'f_tenants.id': 1,
    detailed: true,
    searchMetadata: true,
    size: ITEMS_PER_REQUEST,
    page,
  },
});

const getProgramsParams = ({ query, page, isLoggedIn }) => ({
  method: 'get',
  url: `${api.host}${getAuthorizedEndpointUrl(api.endpoints.searchVodPublic, isLoggedIn)}`,
  appendParams: false,
  params: {
    'f_title.titleORtitle.titleBriefORtitle.summaryShortORtitle.summaryLong': query,
    type: 'VOD_ASSET',
    'f_metadata.contentType': 'EPG_EVENT',
    f_epgEventState: 'CATCHUP',
    sort: 'relevance,videoContent',
    f_deviceType: Platform.getDeviceType(),
    'f_tenants.id': 1,
    detailed: true,
    searchMetadata: true,
    includeVod: true,
    size: ITEMS_PER_REQUEST,
    page,
  },
});

const getChannelsParams = ({ query, page, isLoggedIn }) => ({
  method: 'get',
  url: `${api.host}${getAuthorizedEndpointUrl(api.endpoints.searchChannelPublic, isLoggedIn)}`,
  params: {
    f_title: query,
    size: ITEMS_PER_REQUEST,
    visibilityRights: 'PREVIEW',
    includeVod: true,
    page,
  },
});

const getParamsByCategoryId = (id, data) => {
  if (id === 'films') return getFilmsParams(data);
  if (id === 'series') return getSeriesParams(data);
  if (id === 'channels') return getChannelsParams(data);
  if (id === 'programs') return getProgramsParams(data);
};

export const fetchSearch =
  ({ query }) =>
  async (dispatch, getState) => {
    try {
      dispatch(showLoader());
      dispatch({ type: types.FETCH, payload: { query } });
      const isLoggedIn = getIsLoggedInSelector(getState());
      const page = 0;

      const paramsList = [
        { id: 'films', params: getFilmsParams({ query, page, isLoggedIn }) },
        { id: 'series', params: getSeriesParams({ query, page, isLoggedIn }) },
        { id: 'channels', params: getChannelsParams({ query, page, isLoggedIn }) },
        { id: 'programs', params: getProgramsParams({ query, page, isLoggedIn }) },
      ];

      const results = await Promise.allSettled(
        paramsList.map(({ id, params }) => {
          return requester(params).then(({ content }) => ({ id, content }));
        }),
      );
      const payload = results
        .filter(({ status }) => status === 'fulfilled')
        .map(({ value }) => {
          return {
            ...value,
            isAllItemsWereLoaded: size(value.content) < ITEMS_PER_REQUEST,
          };
        });

      dispatch({ type: types.FETCH_SUCCESS, payload });
    } catch (error) {
      console.error(error);
      dispatch({ type: types.FETCH_ERROR, payload: error });
    } finally {
      dispatch(hideLoader());
    }
  };

export const loadExtraSearchItems = (id) => async (dispatch, getState) => {
  try {
    const state = getState();
    const isLoggedIn = getIsLoggedInSelector(state);
    const query = selectQuery(state);
    const category = selectCategoryById(state, { id });
    const { isOnLazyLoad, isAllItemsWereLoaded, page } = category || {};

    if (isOnLazyLoad || isAllItemsWereLoaded) return;

    dispatch({ type: types.LOAD_EXTRA_ITEMS, payload: { id } });

    const { content } = await requester(getParamsByCategoryId(id, { page: page + 1, isLoggedIn, query }));

    const payload = { id, content, page: page + 1, isAllItemsWereLoaded: isEmpty(content) };

    dispatch({ type: types.LOAD_EXTRA_ITEMS_SUCCESS, payload });
  } catch (error) {
    console.error(error);
    dispatch({ type: types.LOAD_EXTRA_ITEMS, payload: { id, error } });
  }
};

export const resetSearchData = () => ({ type: types.RESET_SERACH_DATA });
